import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogFooter,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Review } from '@/types';
import { format } from 'date-fns';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Send } from "lucide-react"; // Added Send icon
import { toast } from "@/hooks/ui/use-toast";

interface ReviewDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  review: Review | null;
  onSaveReply: (reviewId: string, replyText: string) => Promise<void>;
  onSendReplyEmail: (review: Review, replyText: string) => Promise<void>; // New prop for sending email
}

const ReviewDetailsModal: React.FC<ReviewDetailsModalProps> = ({ isOpen, onClose, review, onSaveReply, onSendReplyEmail }) => {
  const [replyText, setReplyText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false); // State for email sending

  // Initialize replyText when modal opens or review changes
  useEffect(() => {
    if (review) {
      setReplyText(review.reply || ''); // Initialize with existing reply or empty string
    } else {
      setReplyText(''); // Clear if no review
    }
  }, [review]);


  if (!review) {
    return null; // Don't render if no review is selected
  }

  const formattedDate = review.date ? format(new Date(review.date), 'PPP p') : 'N/A'; // Format date

  const handleSave = async () => {
    if (!review) return;
    setIsSaving(true);
    try {
      await onSaveReply(review.id, replyText.trim()); // Call the handler passed via props, trim whitespace
      toast.success("Reply saved successfully!");
      // Don't close automatically, let parent refresh data which might update the modal view
      // onClose(); // Don't close automatically
    } catch (error) {
      console.error("Error saving reply:", error);
      toast.error("Failed to save reply.");
      // Don't reset state here, let the user retry or close
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndSend = async () => {
    if (!review || !review.customerEmail) {
      toast.warning("Cannot send email: Customer email is missing.");
      return;
    }
    if (review.reply_email_sent_at) {
      toast.info("Reply email has already been sent for this review.");
      return;
    }

    setIsSaving(true); // Indicate saving process starts
    setIsSendingEmail(true); // Indicate email sending process starts

    try {
      // 1. Save the reply first
      await onSaveReply(review.id, replyText.trim());
      toast.success("Reply saved successfully!");

      // 2. If save is successful, send the email
      try {
        await onSendReplyEmail(review, replyText.trim()); // Pass the full review object and reply text
        // Success toast is handled in the parent (DashboardContent) after function invoke
      } catch (emailError) {
        // Error toast is handled in the parent (DashboardContent)
        console.error("Error sending reply email (caught in modal):", emailError);
        // Keep modal open for user
      }

    } catch (saveError) {
      // Error saving reply (toast handled in handleSave)
      console.error("Error saving reply before sending email:", saveError);
      // Keep modal open for user
    } finally {
      setIsSaving(false); // Reset saving state
      setIsSendingEmail(false); // Reset sending state
      // Note: The parent component (DashboardContent) should update the review state
      // which will cause this modal to re-render with the updated reply_email_sent_at status
    }
  };


  const emailAlreadySent = !!review?.reply_email_sent_at;
  const canSendEmail = !!review?.customerEmail && !emailAlreadySent;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}> {/* Ensure onClose is called when dialog closes */}
      {/* Apply width directly to DialogContent */}
      <DialogContent className="max-w-3xl"> {/* Set max width */}
        {/* Wrap all content in a new grid container */}
        <div className="grid grid-cols-3 gap-x-8 gap-y-4">
          {/* DialogHeader spans all columns */}
          <DialogHeader className="col-span-3">
            <DialogTitle>Review Details</DialogTitle>
          <DialogDescription>
            Detailed information about the selected review.
          </DialogDescription>
          </DialogHeader>

          {/* Detail items as direct children of the new grid */}
          {/* Row 1 */}
          <div className="flex items-baseline justify-between border-b mt-2"> {/* Added border-b pb-2 */}
            <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-white mr-2">Customer Name:</span>
            <p className="text-sm text-gray-800 dark:text-white">{review.customerName || 'N/A'}</p> {/* Added text-sm */}
          </div>
          <div className="flex items-baseline justify-between border-b mt-2"> {/* Added border-b pb-2 */}
            <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-white mr-2">Date:</span>
            <p className="text-sm text-gray-800 dark:text-white">{formattedDate}</p> {/* Added text-sm */}
          </div>
          <div className="flex items-baseline justify-between border-b mt-2"> {/* Added border-b pb-2 */}
            <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-white mr-2">Rating:</span>
            <p className="text-sm text-gray-800 dark:text-white">{review.rating !== undefined && review.rating !== null ? `${review.rating}/5` : 'N/A'}</p> {/* Added text-sm */}
          </div>

          {/* Row 2 */}
          <div className="flex items-baseline justify-between border-b mt-2"> {/* Added border-b pb-2 */}
            <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-white mr-2">Customer Email:</span>
            <p className="text-sm text-gray-800 dark:text-white">{review.customerEmail || 'N/A'}</p> {/* Added text-sm */}
          </div>
          <div className="flex items-baseline justify-between border-b mt-2"> {/* Added border-b pb-2 */}
            <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-white mr-2">Form:</span>
            <p className="text-sm text-gray-800 dark:text-white">{review.formTitle || 'N/A'}</p> {/* Added text-sm */}
          </div>
          <div className="flex items-baseline justify-between border-b mt-2"> {/* Added border-b pb-2 */}
            <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-white mr-2">Source:</span>
            <p className="text-sm text-gray-800 dark:text-white">{review.source || 'N/A'}</p> {/* Added text-sm */}
          </div>

          {/* Review Text - Span all 3 columns */}
          <div className="col-span-3 mt-4 pt-4 ">
             <span className="block text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-white">Review Text:</span>
             <p className="break-words text-gray-800 dark:text-white mt-1">{review.text || 'No text provided.'}</p> {/* Added mt-1 */}
          </div>

           {/* Existing Reply - Span all 3 columns */}
           {review.reply && (
             <div className="col-span-3 mt-4 pt-4 border-t">
                <span className="block text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-white">Reply:</span>
                <p className="break-words text-gray-800 dark:text-white mt-1">{review.reply || 'No reply yet.'}</p> {/* Added mt-1 */}
             </div>
           )}

          {/* Reply Input Area - Span all 3 columns */}
          <div className="col-span-3 mt-4 pt-4 border-t">
            <Label htmlFor="replyText" className="block text-sm font-medium leading-none pt-1.5 dark:text-white">Reply:</Label>
            <Textarea
              id="replyText"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write your reply here..."
              className="w-full dark:text-white mt-1" // Added margin-top
              rows={4}
            />
          </div>

        {/* DialogFooter spans all columns */}
        <DialogFooter className="col-span-3 mt-4 flex justify-between items-center"> {/* Use flex for layout */}
          <div> {/* Left side for status */}
            {emailAlreadySent && (
              <span className="text-sm text-green-600 dark:text-green-400 font-medium">Reply Email Sent</span>
            )}
             {!review?.customerEmail && !emailAlreadySent && (
               <span className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Cannot send email (no customer email)</span>
             )}
          </div>
          <div className="flex space-x-2"> {/* Right side for buttons */}
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving || isSendingEmail || emailAlreadySent} // Disable if sending email or already sent
            >
              {isSaving && !isSendingEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isSaving && !isSendingEmail ? 'Saving...' : 'Save Reply'}
            </Button>
            <Button
              onClick={handleSaveAndSend}
              disabled={!canSendEmail || isSaving || isSendingEmail}
              title={!review?.customerEmail ? "Customer email missing" : emailAlreadySent ? "Email already sent" : "Save reply and send email notification"}
            >
              {isSendingEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {isSendingEmail ? 'Sending...' : 'Save & Send Email'}
            </Button>
          </div>
        </DialogFooter>
        </div> {/* End of the new grid container */}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewDetailsModal;
